import React from 'react'
import { Typography } from 'antd'
import {
    CalendarOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import ModalWrapper from './ModalWrapper'
import { BasicModalProps } from '../../types/modals'
import { useModalDataStore } from '../../store/ModalDataStore'
import { EventData } from '../../types/events'
import './SportEventModal.css'
import './Modals.css'
import StoreButtons from '../Common/Buttons/StoreButtons'
import { formatEventDate } from '../../utils/dateUtils'

const { Title, Paragraph } = Typography

function isEventData(data: unknown): data is EventData {
    return typeof data === 'object' && data !== null && 'eventDate' in data
}

export const SportEventModal: React.FC<BasicModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation()
    const data = useModalDataStore((state) => state.data)

    if (!data || !isEventData(data)) {
        return null
    }
    const eventData = data

    const { date, time } = formatEventDate(eventData.eventDate)
    return (
        <ModalWrapper isOpen={isOpen} onClose={onClose} width={862}>
            <div data-testid="event-modal" className="modal-content">
                <div className="sportevent-modal-container">
                    <div className="sportevent-modal-content">
                        <div className="text-container">
                            <Title className="title" level={2}>
                                {eventData.sportType.sportName}
                            </Title>
                            <Paragraph className="subtitle-paragraph">
                                {eventData.title}
                            </Paragraph>

                            <div className="card-metadata">
                                <div className="metadata-item">
                                    <CalendarOutlined />
                                    <span>{t('modals.sportEvent.date')}</span>
                                    <span>{date}</span>
                                </div>
                                <div className="metadata-item">
                                    <ClockCircleOutlined />
                                    <span>{t('modals.sportEvent.time')}</span>
                                    <span>{time}</span>
                                </div>
                                <div className="metadata-item">
                                    <EnvironmentOutlined />
                                    <span>
                                        {t('modals.sportEvent.location')}
                                    </span>
                                    <span>{eventData.place.name}</span>
                                </div>
                            </div>

                            <Paragraph className="paragraph">
                                {t('modals.sportEvent.description', {
                                    sportName:
                                        eventData.sportType.sportName.toLowerCase(),
                                })}
                            </Paragraph>

                            <div className="qr-store-buttons">
                                <div className="qr-code">
                                    <img
                                        src="/img/qrcode.webp"
                                        alt={t('common.alt.qrCode')}
                                    />
                                </div>
                                <StoreButtons main="store-buttons" />
                            </div>
                        </div>
                        <div className="image-group">
                            <div className="image">
                                <img
                                    src={
                                        eventData.image ||
                                        '/img/whatis-Football.png'
                                    }
                                    alt={
                                        eventData.sportType.sportName ||
                                        t('common.alt.football')
                                    }
                                    onError={(e) => {
                                        const target =
                                            e.target as HTMLImageElement
                                        if (
                                            target.src !==
                                            '/img/whatis-Football.png'
                                        ) {
                                            target.src =
                                                '/img/whatis-Football.png'
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="single-mobile-image">
                            <img
                                src={eventData.image || '/img/Footballcard.png'}
                                alt={
                                    eventData.sportType.sportName
                                        ? `${eventData.sportType.sportName} ${t('common.alt.mobile')}`
                                        : t('common.alt.sportEventMobile')
                                }
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    if (
                                        target.src !== '/img/Footballcard.png'
                                    ) {
                                        target.src = '/img/Footballcard.png'
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </ModalWrapper>
    )
}
